import React, { useState, useEffect } from 'react';
import { FiX, FiUser, FiLock, FiImage, FiBriefcase, FiBell } from 'react-icons/fi';
import NotificationsSettings from './NotificationsSettings';

const ProfileModal = ({ isOpen, onClose, user, token, onUpdate, workspace }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [profileData, setProfileData] = useState({ name: user?.name || '' });
  const [companyData, setCompanyData] = useState({ name: workspace?.name || '' });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (isOpen) {
      setCompanyData({ name: workspace?.name || '' });
    }
  }, [isOpen, workspace]);

  if (!isOpen) return null;

  const isAdmin = user?.role === 'Admin';

  const handleProfileUpdate = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: profileData.name,
          avatar: avatarUrl
        })
      });

      if (response.ok) {
        setSuccess('Profile updated successfully!');
        onUpdate();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update profile');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyUpdate = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/workspace`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: companyData.name
        })
      });

      if (response.ok) {
        setSuccess('Company name updated successfully! Refresh to see changes.');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update company name');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async () => {
    setError('');
    setSuccess('');

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        setSuccess('Password updated successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to update password');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Profile Settings</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>
        
        <div style={{ borderBottom: '1px solid var(--border-light)', padding: '0 32px', display: 'flex', gap: '24px' }}>
          <button
            className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <FiUser size={16} />
            <span>Profile</span>
          </button>
          {isAdmin && (
            <button
              className={`tab-btn ${activeTab === 'company' ? 'active' : ''}`}
              onClick={() => setActiveTab('company')}
            >
              <FiBriefcase size={16} />
              <span>Company</span>
            </button>
          )}
          <button
            className={`tab-btn ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <FiLock size={16} />
            <span>Password</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'avatar' ? 'active' : ''}`}
            onClick={() => setActiveTab('avatar')}
          >
            <FiImage size={16} />
            <span>Avatar</span>
          </button>
          <button
            className={`tab-btn ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
            data-testid="notifications-tab"
          >
            <FiBell size={16} />
            <span>Notifications</span>
          </button>
        </div>
        
        <div className="modal-body">
          {activeTab === 'company' && isAdmin && (
            <>
              <div className="form-group">
                <label className="form-label">Company/Organization Name</label>
                <input
                  type="text"
                  className="input"
                  value={companyData.name}
                  onChange={(e) => setCompanyData({ name: e.target.value })}
                  data-testid="company-name-input"
                />
                <p className="form-helper">This name appears in the sidebar and welcome message</p>
              </div>
              <div style={{ 
                padding: '12px', 
                backgroundColor: '#fff3e0', 
                borderRadius: '6px',
                fontSize: '13px',
                color: '#663c00'
              }}>
                <strong>Note:</strong> Changing the company name will update it for all users in your workspace.
              </div>
            </>
          )}
          
          {activeTab === 'profile' && (
            <>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="input"
                  value={profileData.name}
                  onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                  data-testid="profile-name-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="input"
                  value={user?.email}
                  disabled
                  style={{ backgroundColor: 'var(--gray-50)' }}
                />
                <p className="form-helper">Email cannot be changed</p>
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <input
                  type="text"
                  className="input"
                  value={user?.role}
                  disabled
                  style={{ backgroundColor: 'var(--gray-50)' }}
                />
                <p className="form-helper">Contact admin to change role</p>
              </div>
            </>
          )}
          
          {activeTab === 'password' && (
            <>
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input
                  type="password"
                  className="input"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  data-testid="current-password-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="input"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  data-testid="new-password-input"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className="input"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  data-testid="confirm-password-input"
                />
              </div>
            </>
          )}
          
          {activeTab === 'avatar' && (
            <>
              <div className="form-group">
                <label className="form-label">Avatar URL</label>
                <input
                  type="url"
                  className="input"
                  value={avatarUrl}
                  onChange={(e) => setAvatarUrl(e.target.value)}
                  placeholder="https://example.com/avatar.jpg"
                  data-testid="avatar-url-input"
                />
                <p className="form-helper">Enter an image URL for your avatar</p>
              </div>
              <div className="form-group">
                <label className="form-label">Preview</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt="Avatar"
                      style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="avatar" style={{ width: '80px', height: '80px', fontSize: '32px' }}>
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                  <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {avatarUrl ? 'Custom avatar' : 'Default avatar'}
                  </div>
                </div>
              </div>
            </>
          )}
          
          {activeTab === 'notifications' && (
            <NotificationsSettings token={token} />
          )}

          {error && <div className="form-error">{error}</div>}
          {success && <div style={{ color: '#1DBF73', fontSize: '14px', marginTop: '12px' }}>{success}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Close
          </button>
          {activeTab !== 'notifications' && (
            <button 
              className="btn btn-primary" 
              onClick={
                activeTab === 'password' ? handlePasswordUpdate : 
                activeTab === 'company' ? handleCompanyUpdate :
                handleProfileUpdate
              }
              disabled={loading}
              data-testid="save-profile-button"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;